/*
 * dashcam/overlay.js — the stamp burned into every frame: date and time,
 * speed, coordinates and the owner's own text (a plate number).
 *
 * It is drawn onto the same canvas the encoder reads, so the file carries it —
 * a stamp shown only in the player would be lost the moment the file is sent
 * to an insurer or uploaded anywhere. Formatting is kept pure (no canvas) so
 * the numbers are testable; drawing takes a 2D context.
 *
 * Time is written with Latin digits and a 24-hour clock in every locale: a
 * stamp is read by other people (police, an insurer, a court), often in
 * another country, and "٢٦/٠٩" or "2:03 PM" are exactly the ambiguities a
 * timestamp exists to remove. The date ORDER follows the locale.
 *
 * The speed sits on a solid amber badge. It is the stamp's anchor — the thing a
 * viewer looks for first — and it is also what the page's own test looks for in
 * the saved file's pixels (tests/playwright/dashcam-e2e.mjs), so a recorder that
 * forgot the overlay cannot pass for one that burned it in.
 *
 * Guards: tests/js/dashcam-overlay.test.mjs.
 */

export const BADGE = Object.freeze({ fill: '#ffb000', text: '#111111' });

const UNIT_FACTOR = { kmh: 3.6, mph: 2.2369362920544 };

/** '58 km/h'; '— km/h' while there is no fix. */
export function speedText(mps, unit, labels) {
    const u = unit === 'mph' ? 'mph' : 'kmh';
    const label = (labels && labels[u]) || (u === 'mph' ? 'mph' : 'km/h');
    if (mps == null || !isFinite(mps)) return '— ' + label;
    const v = Math.max(0, mps * UNIT_FACTOR[u]);
    return Math.round(v) + ' ' + label;
}

/** '55.75580, 37.61730' — decimal degrees that paste straight into any map. */
export function coordText(lat, lon) {
    if (lat == null || lon == null || !isFinite(lat) || !isFinite(lon)) return '';
    return lat.toFixed(5) + ', ' + lon.toFixed(5);
}

/**
 * A clock for one locale: ms → '26.09.2026 14:03:22'. Cached per second —
 * the pump asks 30 times a second.
 */
export function makeClock(locale) {
    let fmt;
    try {
        fmt = new Intl.DateTimeFormat((locale || 'en') + '-u-nu-latn', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch (_) { fmt = null; }
    const p = (n) => (n < 10 ? '0' : '') + n;
    let lastSec = null, lastText = '';
    return function (ms) {
        const sec = Math.floor(ms / 1000);
        if (sec === lastSec) return lastText;
        lastSec = sec;
        const d = new Date(sec * 1000);
        // the date in the locale's order (only its digits and separators: some locales
        // wrap the pieces in bidi marks); the time always HH:MM:SS — Indonesian
        // writes 14.03.22 and English 2:03 PM, neither belongs on evidence
        const date = fmt
            ? fmt.format(d).replace(/[^0-9./\- ]/g, '').trim()
            : d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
        lastText = date + ' ' + p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
        return lastText;
    };
}

/**
 * The pieces of one frame's stamp. `on` — which parts are switched on.
 * → [{kind:'time'|'speed'|'coords'|'text', text}]
 */
export function stampParts(st, on, clock, labels) {
    const parts = [];
    if (on.time) parts.push({ kind: 'time', text: clock(st.now) });
    if (on.speed) parts.push({ kind: 'speed', text: speedText(st.mps, st.unit, labels) });
    if (on.coords) { const c = coordText(st.lat, st.lon); if (c) parts.push({ kind: 'coords', text: c }); }
    if (on.text && st.text) parts.push({ kind: 'text', text: String(st.text).slice(0, 32) });
    return parts;
}

/**
 * Where everything goes on a W×H frame. `measure(text, fontPx)` → width.
 * One row when it fits, two when it does not (a portrait frame, a long text).
 * → {band:{x,y,w,h}, font, items:[{kind,text,x,y,w,h,badge?}]}
 */
export function layoutStamp(W, H, parts, measure) {
    if (!parts.length) return { band: null, font: 0, items: [] };
    const short = Math.min(W, H);
    const rowH = Math.max(20, Math.round(short * 0.062));
    const font = Math.max(12, Math.round(rowH * 0.6));
    const pad = Math.round(rowH * 0.4);
    const gap = Math.round(rowH * 0.55);
    const widths = parts.map(p => Math.ceil(measure(p.text, font)) + (p.kind === 'speed' ? pad * 2 : 0));
    const total = widths.reduce((s, w) => s + w, 0) + gap * (parts.length - 1) + pad * 2;
    let rows;
    if (total <= W) rows = [parts.map((p, i) => i)];
    else {
        // time + speed on the first row, the rest on the second
        const first = [], second = [];
        parts.forEach((p, i) => ((p.kind === 'time' || p.kind === 'speed') ? first : second).push(i));
        rows = second.length && first.length ? [first, second] : [parts.map((p, i) => i)];
    }
    const bandH = rowH * rows.length + Math.round(rowH * 0.2);
    const band = { x: 0, y: H - bandH, w: W, h: bandH };
    const items = [];
    rows.forEach((row, ri) => {
        let x = pad;
        const y = band.y + Math.round(rowH * 0.1) + ri * rowH;
        row.forEach((i) => {
            const p = parts[i];
            // a text wider than what is left of the row is cut, never drawn past the frame
            const w = Math.min(widths[i], Math.max(0, W - pad - x));
            const it = { kind: p.kind, text: p.text, x, y, w, h: rowH };
            if (p.kind === 'speed') it.badge = { x, y: y + Math.round(rowH * 0.08), w, h: rowH - Math.round(rowH * 0.16) };
            items.push(it);
            x += w + gap;
        });
    });
    return { band, font, items };
}

/** Paint the stamp. Returns the layout (the page keeps it for its own test). */
export function drawStamp(ctx, W, H, parts) {
    const fontFor = (px) => '600 ' + px + 'px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
    const measure = (text, px) => { ctx.font = fontFor(px); return ctx.measureText(text).width; };
    const L = layoutStamp(W, H, parts, measure);
    if (!L.band) return L;
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(L.band.x, L.band.y, L.band.w, L.band.h);
    ctx.font = fontFor(L.font);
    ctx.textBaseline = 'middle';
    ctx.direction = 'ltr';
    ctx.textAlign = 'left';
    for (const it of L.items) {
        const cy = it.y + it.h / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(it.x, it.y, it.w, it.h);
        ctx.clip();
        if (it.badge) {
            ctx.fillStyle = BADGE.fill;
            ctx.fillRect(it.badge.x, it.badge.y, it.badge.w, it.badge.h);
            ctx.fillStyle = BADGE.text;
            ctx.fillText(it.text, it.x + Math.round(it.h * 0.4), cy);
        } else {
            ctx.fillStyle = '#ffffff';
            ctx.fillText(it.text, it.x, cy);
        }
        ctx.restore();
    }
    ctx.restore();
    return L;
}
